<script>
import _ from "lodash";
import moment from "moment";
import VimeoVideo from "./VimeoVideo";

export default {
  name: "Lecture",
  components: { VimeoVideo },
  props: {
    lecture: {
      type: Object
    },
    downloads: {
      type: Object
    }
  },
  data() {
    return {
      currentTime: 0,
      skipAmount: 10,
      playbackSpeed: 1,
      playbackSpeeds: [
        { text: "0.5x", value: 0.5 },
        { text: "1.0x", value: 1.0 },
        { text: "1.25x", value: 1.25 },
        { text: "1.5x", value: 1.5 },
        { text: "1.75x", value: 1.75 },
        { text: "2.0x", value: 2.0 }
      ]
    };
  },
  computed: {
    downloadFiles() {
      const files = this.downloads?.root?.mediaItem || [];

      if (!Array.isArray(files)) {
        return _.values(files);
      } else {
        return _.map(files, f => f.File);
      }
    }
  },
  mounted() {
    if (localStorage) {
      if (localStorage.videoSkipAmount) {
        this.skipAmount = localStorage.videoSkipAmount;
      }

      if (localStorage.videoPlaybackSpeed) {
        this.playbackSpeed = localStorage.videoPlaybackSpeed;
      }
    }
  },
  watch: {
    skipAmount(newAmount) {
      if (localStorage) {
        localStorage.videoSkipAmount = newAmount;
      }
    },
    playbackSpeed(newAmount) {
      if (localStorage) {
        localStorage.videoPlaybackSpeed = newAmount;
      }

      this.$refs.vimeoVideo.player.setPlaybackRate(newAmount);
    }
  },
  methods: {
    formatDate(date) {
      return moment(date).format("MM/DD/YYYY");
    },
    formatBytes(bytes, decimals = 2) {
      if (bytes === 0) return "0 Bytes";

      const k = 1024;
      const dm = decimals < 0 ? 0 : decimals;
      const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
    },
    handleTimeUpdate(data) {
      this.currentTime = data.seconds;
    },
    skipForward() {
      const newTime = this.currentTime + parseInt(this.skipAmount);
      console.log(
        `CurrentTime: ${this.currentTime} SkipAmount: ${this.skipAmount} NewTime: ${newTime}`
      );
      this.$refs.vimeoVideo.player.setCurrentTime(newTime);
    },
    rewind() {
      const newTime = this.currentTime - parseInt(this.skipAmount);
      console.log(
        `CurrentTime: ${this.currentTime} SkipAmount: ${this.skipAmount} NewTime: ${newTime}`
      );
      this.$refs.vimeoVideo.player.setCurrentTime(newTime);
    }
  }
};
</script>

<template>
  <div class="py-2 px-3">
    <h1 class="text-blue-700 font-semibold text-3xl">{{ lecture.Name }}</h1>
    <div class="my-2 flex items-center justify-between">
      <a
        :href="lecture.Url"
        target="_blank"
        class="py-2 px-4 my-2 bg-transparent hover:bg-blue-500 text-blue-700 font-semibold hover:text-white border border-blue-500 hover:border-transparent rounded"
      >
        Open in new Tab</a
      >
    </div>

    <div class="flex">
      <div class="video-player">
        <VimeoVideo
          ref="vimeoVideo"
          :videoId="lecture.VimeoVideo"
          @timeupdate="handleTimeUpdate"
          playerHeight="400"
          playerWidth="500"
        />

        <div class="video-controls my-3">
          <div>
            <button
              @click.prevent="rewind"
              class="py-2 px-4 mx-2 bg-transparent hover:bg-blue-500 text-blue-700 font-semibold hover:text-white inline-block border border-blue-500 hover:border-transparent rounded"
            >
              Skip Back
            </button>

            <label class="font-bold" for="skipAmount">Skip By (sec)</label>
            <input
              id="skipAmount"
              v-model="skipAmount"
              type="number"
              minimum="0"
              class="py-2 px-4 mx-2 w-20 bg-white focus:outline-none focus:shadow-outline border border-gray-300 rounded-lg inline-block appearance-none leading-normal"
            />

            <button
              @click.prevent="skipForward"
              class="py-2 px-4 mx-2 bg-transparent hover:bg-blue-500 text-blue-700 font-semibold hover:text-white inline-block border border-blue-500 hover:border-transparent rounded"
            >
              Skip Ahead
            </button>
          </div>
          <div class="py-3 px-3">
            <label class="font-bold" for="playbackSpeed">Speed</label>
            <select
              id="playbackSpeed"
              v-model="playbackSpeed"
              class="py-3 px-4 pr-2 ml-2 appearance-none bg-gray-200 border border-gray-200 text-gray-700 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
            >
              <option
                v-for="speed in playbackSpeeds"
                :key="speed.value"
                :value="speed.value"
                :selected="speed.value === playbackSpeed"
                >{{ speed.text }}</option
              >
            </select>
          </div>
        </div>
      </div>

      <div class="side-bar mx-2">
        <div v-if="lecture.BodyText" class="description">
          <div v-html="lecture.BodyText" class="my-4"></div>
        </div>

        <div class="my-6">
          <h3 class="text-2xl">Downloads</h3>
          <ul v-if="downloadFiles.length > 0">
            <li v-for="file in downloadFiles" :key="file['@id']" class="my-3">
              <div
                v-if="file['@nodeTypeAlias'] === 'File'"
                class="flex content-start justify-between border border-gray-400 rounded py-3 px-3"
              >
                <div>
                  <div>
                    <strong>{{ file["@nodeName"] }}</strong>
                  </div>
                  <div>{{ formatBytes(file.umbracoBytes) }}</div>
                </div>
                <a
                  :href="file.umbracoFile"
                  target="_blank"
                  class="py-2 px-4 my-2 bg-transparent hover:bg-blue-500 text-blue-700 font-semibold hover:text-white border border-blue-500 hover:border-transparent rounded"
                >
                  Download
                </a>
              </div>
              <div v-if="file['@nodeTypeAlias'] === 'Image'">
                <img :src="file.umbracoFile" />
              </div>
            </li>
          </ul>
          <div v-else>
            <p>There are no downloads for this lecture.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style module lang="scss"></style>
